import React, { useState, useEffect } from 'react';
import { FiCpu, FiChevronDown, FiChevronUp, FiZap, FiPlay, FiCheck, FiClock, FiAlertCircle, FiArrowLeft, FiArrowRight, FiLock, FiDatabase, FiUploadCloud, FiRadio } from 'react-icons/fi';
import { getApiUrl, extractErrorMessage } from '../../utils/api';
import { useLaunchNotification } from '../../contexts/LaunchNotificationContext';

interface AvailableInstance {
  instance_id: string;
  provider: string;
  instance_type: string;
  region: string;
  gpu_type: string;
  launch_time: string;
  hourly_cost: number;
  has_notebook: boolean;
  project_name: string;
  status: string;
}

interface SemanticGPUOption {
  provider: 'aws' | 'gcp';
  provider_instance_id: string;
  gpu_model_name?: string;
  total_gpu_memory_gb?: number;
  on_demand_price_per_hour?: number;
  region: string;
  performance_category?: string;
  cost_tier?: string;
  display_name?: string;
}

interface NotebookLauncherProps {
  isOpen: boolean;
  onClose: () => void;
  instances?: AvailableInstance[];
  preSelectedInstanceId?: string | null;
  onLaunch: (instanceId: string, notebookPath?: string) => void;
  onNavigateToCompute?: () => void;
  notebookName?: string;
  notebookPath?: string;
  onLaunchedNewInstance?: () => void;
}

const LAST_INSTANCE_KEY = 'propalgos_last_instance';

const NotebookLauncher: React.FC<NotebookLauncherProps> = ({
  isOpen,
  onClose,
  instances: propsInstances,
  preSelectedInstanceId,
  onLaunch,
  onNavigateToCompute,
  notebookName,
  notebookPath,
  onLaunchedNewInstance
}) => {
  const { addLaunch } = useLaunchNotification();
  const [instances, setInstances] = useState<AvailableInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);

  // GPU selection state (when no running instances)
  const [gpuOptions, setGpuOptions] = useState<SemanticGPUOption[]>([]);
  const [loadingGpus, setLoadingGpus] = useState(false);
  const [selectedGpuIndex, setSelectedGpuIndex] = useState<number>(0);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Recommended GPU tier hint from the Research Feed post-generate deep link.
  // Populated by ComputePage when it parses `?tier=…` and stashes under
  // `pending_tier_hint_for_next_launch`. Read once at mount, cleared on
  // consumption (but preserved in a local state so the "Recommended by
  // paper" badge survives re-renders).
  const [recommendedTier] = useState<string | null>(() => {
    const v = sessionStorage.getItem('pending_tier_hint_for_next_launch');
    if (v) sessionStorage.removeItem('pending_tier_hint_for_next_launch');
    return v;
  });
  const [recommendedGpuIndex, setRecommendedGpuIndex] = useState<number | null>(null);

  // Multi-step flow
  const [step, setStep] = useState<'compute' | 'data-sources'>('compute');

  // Load instances and auto-select
  useEffect(() => {
    const loadInstances = async () => {
      if (propsInstances && propsInstances.length > 0) {
        setInstances(propsInstances);
        autoSelectInstance(propsInstances);
      } else {
        setLoading(true);
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${getApiUrl()}/api/v1/notebooks/available-instances`, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
          });
          if (response.ok) {
            const data = await response.json();
            setInstances(data);
            autoSelectInstance(data);

            // If no instances, fetch GPU options
            if (data.length === 0) {
              fetchGpuOptions();
            }
          } else {
            // No instances available, fetch GPU options
            fetchGpuOptions();
          }
        } catch (err) {
          console.error('Error fetching instances:', err);
          // Fetch GPU options as fallback
          fetchGpuOptions();
        } finally {
          setLoading(false);
        }
      }
      setLaunching(false);
      setLaunchError(null);
    };

    if (isOpen) {
      loadInstances();
      setStep('compute');
    }
  }, [isOpen, propsInstances, preSelectedInstanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch GPU options when no instances are available
  const fetchGpuOptions = async () => {
    setLoadingGpus(true);
    try {
      const token = localStorage.getItem('authToken');
      const filters = encodeURIComponent(JSON.stringify({
        performance_categories: ['training_standard', 'inference_optimized'],
        cost_tiers: ['budget', 'medium'],
        providers: ['aws', 'gcp'],
        sort_by: 'price_performance',
      }));

      const response = await fetch(`${getApiUrl()}/api/gpu/discover?filters=${filters}&limit=4`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setGpuOptions(data);
        // If a research-feed deep link hinted at a tier (e.g. `t4`), try to
        // preselect the first option whose GPU model name matches. CPU hints
        // never match GPU-only options here, so fall through to index 0.
        const matchIdx = recommendedTier && recommendedTier !== 'cpu'
          ? data.findIndex((g: SemanticGPUOption) =>
              (g.gpu_model_name ?? '').toLowerCase().includes(recommendedTier)
            )
          : -1;
        if (matchIdx >= 0) {
          setSelectedGpuIndex(matchIdx);
          setRecommendedGpuIndex(matchIdx);
        } else {
          setSelectedGpuIndex(0);
          setRecommendedGpuIndex(null);
        }
      } else {
        // Try fallback catalog endpoint
        const fallbackResponse = await fetch(`${getApiUrl()}/api/gpu/catalog?limit=4`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          setGpuOptions(fallbackData.instances || []);
        }
      }
    } catch (err) {
      console.error('Error fetching GPU options:', err);
    } finally {
      setLoadingGpus(false);
    }
  };

  // Launch new instance with selected GPU
  const handleLaunchNewInstance = async () => {
    const selectedGpu = gpuOptions[selectedGpuIndex];
    if (!selectedGpu) return;

    setLaunching(true);
    setLaunchError(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/compute/launch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: selectedGpu.provider,
          instance_type: selectedGpu.provider_instance_id,
          region: selectedGpu.region,
          pricing_type: 'ondemand',
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // Use the actual cloud instance_id from the nested instance object
        // (not session_id which is a different identifier)
        const instanceId = result.instance?.instance_id || result.instance_id || result.session_id;

        // Persist to sessionStorage as bridge — React state can be lost before
        // "Open Notebook" is clicked in the toast notification.
        // Also promote any research-feed deep-link notebook hint to the
        // instance-keyed sentinel so the same downstream code picks it up.
        const pendingFromDeepLink = sessionStorage.getItem('pending_notebook_for_next_launch');
        const effectiveNotebook = notebookPath || pendingFromDeepLink || undefined;
        if (effectiveNotebook) {
          sessionStorage.setItem(`pending_notebook_for_${instanceId}`, effectiveNotebook);
        }
        if (pendingFromDeepLink) {
          sessionStorage.removeItem('pending_notebook_for_next_launch');
        }

        // First-ever launch gets a longer-form explainer + research-feed CTA
        // so the 2-minute cold start isn't a silent wait. Gated on
        // localStorage so returning users see the normal compact toast.
        // Pairs with the existing loading-screen nudge in NativeNotebookPage.
        const FIRST_LAUNCH_KEY = 'has_seen_first_launch_copy';
        const isFirstLaunch = !localStorage.getItem(FIRST_LAUNCH_KEY);
        const firstLaunchCopy = isFirstLaunch
          ? {
              explainer:
                "First time takes a little longer while we start up a fresh GPU and load your workspace. While you wait, skim this week's quant research roll-up for ideas to try.",
              ctaLabel: 'Open Research Feed',
              ctaHref: '/dashboard/research-feed',
            }
          : {};
        if (isFirstLaunch) {
          try { localStorage.setItem(FIRST_LAUNCH_KEY, '1'); } catch { /* storage unavailable */ }
        }

        // Add launch notification - this will poll for status and show when ready
        addLaunch({
          instanceId,
          provider: selectedGpu.provider,
          instanceType: selectedGpu.provider_instance_id,
          gpuType: selectedGpu.gpu_model_name || selectedGpu.display_name,
          notebookPath: notebookPath,
          ...firstLaunchCopy,
        });

        // Close the modal - user will see the notification in the corner
        if (onLaunchedNewInstance) {
          onLaunchedNewInstance();
        } else {
          onClose();
        }
      } else if (response.status === 402) {
        window.location.href = '/dashboard/billing?reason=insufficient_balance';
      } else {
        const errData = await response.json().catch(() => ({}));
        setLaunchError(extractErrorMessage(errData, 'Failed to launch instance. Please try again.'));
      }
    } catch (err) {
      console.error('Launch error:', err);
      setLaunchError('Network error. Please check your connection and try again.');
    } finally {
      setLaunching(false);
    }
  };

  const getBestForText = (gpu: SemanticGPUOption): string => {
    const vram = gpu.total_gpu_memory_gb ?? 0;
    if (vram <= 16) return 'Getting started, small backtests';
    if (vram <= 24) return 'Most strategies, ML training';
    return 'Large ML models, complex backtests';
  };

  const isRecommended = (index: number): boolean => {
    if (gpuOptions.length === 0) return false;
    const firstGoodOption = gpuOptions.findIndex(g => (g.total_gpu_memory_gb ?? 0) >= 24);
    return index === (firstGoodOption >= 0 ? firstGoodOption : 0);
  };

  const autoSelectInstance = (availableInstances: AvailableInstance[]) => {
    if (availableInstances.length === 0) return;

    // Highest priority: instance explicitly clicked by the user
    if (preSelectedInstanceId) {
      const preSelected = availableInstances.find(i => i.instance_id === preSelectedInstanceId);
      if (preSelected) {
        setSelectedInstance(preSelected.instance_id);
        return;
      }
    }

    // Try to use last used instance
    const lastInstanceId = localStorage.getItem(LAST_INSTANCE_KEY);
    const lastInstance = availableInstances.find(i => i.instance_id === lastInstanceId);

    if (lastInstance) {
      setSelectedInstance(lastInstance.instance_id);
    } else {
      // Select first available instance
      setSelectedInstance(availableInstances[0].instance_id);
    }
  };

  const handleLaunch = () => {
    if (selectedInstance) {
      setLaunching(true);
      // Remember this instance for next time
      localStorage.setItem(LAST_INSTANCE_KEY, selectedInstance);
      onLaunch(selectedInstance, notebookPath);
    }
  };

  const handleQuickLaunch = (instanceId: string) => {
    setLaunching(true);
    localStorage.setItem(LAST_INSTANCE_KEY, instanceId);
    onLaunch(instanceId, notebookPath);
  };

  if (!isOpen) return null;

  const selectedInstanceData = instances.find(i => i.instance_id === selectedInstance);
  const hasMultipleInstances = instances.length > 1;

  const canProceedFromCompute = instances.length > 0 ? !!selectedInstance : gpuOptions.length > 0;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg max-w-lg w-full mx-4 overflow-hidden border border-gray-800">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">
              {notebookName ? `Launch "${notebookName}"` : 'Launch Notebook'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step indicator */}
          {!loading && (instances.length > 0 || gpuOptions.length > 0) && (
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={() => setStep('compute')}
                className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
                  step === 'compute' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  step === 'compute' ? 'bg-white text-black' : 'bg-gray-700 text-gray-400'
                }`}>1</span>
                Compute
              </button>
              <div className="w-8 h-px bg-gray-700" />
              <div
                className={`flex items-center gap-1.5 text-xs font-medium ${
                  step === 'data-sources' ? 'text-white' : 'text-gray-500'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  step === 'data-sources' ? 'bg-white text-black' : 'bg-gray-700 text-gray-400'
                }`}>2</span>
                Data Sources
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'compute' ? (
            <>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
                  <p className="text-gray-400 mt-3">Finding available instances...</p>
                </div>
              ) : instances.length === 0 ? (
                /* No instances - show GPU selection */
                <div>
                  <p className="text-sm text-gray-400 mb-4">
                    No running instances. Choose a GPU to launch:
                  </p>

                  {loadingGpus ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="w-full p-4 rounded-lg border border-gray-800 bg-gray-800/50 animate-pulse">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5 w-5 h-5 rounded-full border-2 border-gray-600" />
                              <div>
                                <div className="h-4 w-24 bg-gray-700 rounded mb-2" />
                                <div className="h-3 w-32 bg-gray-700 rounded mb-1" />
                                <div className="h-3 w-40 bg-gray-700 rounded" />
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="h-6 w-12 bg-gray-700 rounded mb-1" />
                              <div className="h-3 w-8 bg-gray-700 rounded" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : gpuOptions.length === 0 ? (
                    <div className="text-center py-6">
                      <FiAlertCircle className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400 mb-4">Unable to load GPU options</p>
                      <button
                        onClick={() => {
                          onClose();
                          onNavigateToCompute?.();
                        }}
                        className="px-6 py-2.5 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors"
                      >
                        Go to Compute Page
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {gpuOptions.slice(0, 3).map((gpu, index) => (
                        <button
                          key={`${gpu.provider}-${gpu.provider_instance_id}`}
                          onClick={() => setSelectedGpuIndex(index)}
                          className={`w-full p-4 rounded-lg border transition-all text-left ${
                            selectedGpuIndex === index
                              ? 'border-green-500 bg-green-500/10'
                              : 'border-gray-800 bg-gray-800/50 hover:border-gray-700'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                selectedGpuIndex === index ? 'border-green-500 bg-green-500' : 'border-gray-600'
                              }`}>
                                {selectedGpuIndex === index && <FiCheck className="w-3 h-3 text-white" />}
                              </div>

                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-white font-medium">
                                    {gpu.display_name || gpu.provider_instance_id}
                                  </span>
                                  <span className={`px-1.5 py-0.5 text-xs rounded ${
                                    gpu.provider === 'aws'
                                      ? 'bg-orange-500/20 text-orange-400'
                                      : 'bg-blue-500/20 text-blue-400'
                                  }`}>
                                    {gpu.provider.toUpperCase()}
                                  </span>
                                  {isRecommended(index) && (
                                    <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                                      Recommended
                                    </span>
                                  )}
                                  {recommendedGpuIndex === index && (
                                    <span
                                      title="Recommended by the research paper that generated this notebook"
                                      className="px-1.5 py-0.5 bg-indigo-500/20 text-indigo-300 text-xs rounded"
                                    >
                                      Recommended by paper
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 flex items-center gap-3 text-sm text-gray-400">
                                  <span className="flex items-center gap-1">
                                    <FiCpu className="w-3.5 h-3.5" />
                                    {gpu.gpu_model_name || 'GPU'}
                                  </span>
                                  <span>{gpu.total_gpu_memory_gb ?? 0}GB</span>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">{getBestForText(gpu)}</p>
                              </div>
                            </div>

                            <div className="text-right">
                              <div className="text-lg font-semibold text-white">
                                ${(gpu.on_demand_price_per_hour ?? 0).toFixed(2)}
                              </div>
                              <div className="text-xs text-gray-500">/hour</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Error message */}
                  {launchError && (
                    <div className="mt-4 flex items-center gap-2 text-red-400 text-sm bg-red-900/20 rounded-lg p-3">
                      <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{launchError}</span>
                    </div>
                  )}

                  {/* Footer info */}
                  {gpuOptions.length > 0 && (
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
                      <div className="flex items-center gap-2">
                        <FiClock className="w-4 h-4" />
                        <span>Auto-shutdown after 15 min idle</span>
                      </div>
                      <span title="Billing starts when the instance is created, including boot time (~1-3 min)">Billed from launch</span>
                    </div>
                  )}
                </div>
              ) : (
                /* Has instances - show quick launch */
                <div>
                  {/* Selected Instance Preview */}
                  {selectedInstanceData && (
                    <div className="bg-gray-800 rounded-md p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-700 rounded-md flex items-center justify-center">
                            <FiCpu className="w-5 h-5 text-indigo-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {selectedInstanceData.project_name || selectedInstanceData.gpu_type}
                            </p>
                            <p className="text-sm text-gray-400">
                              {selectedInstanceData.instance_type} • {selectedInstanceData.provider.toUpperCase()} • ${(selectedInstanceData.hourly_cost ?? 0).toFixed(2)}/hr
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-xs text-green-400 bg-green-900/30 px-2 py-1 rounded">
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                            Running
                          </span>
                        </div>
                      </div>

                      {selectedInstanceData.has_notebook && (
                        <p className="text-xs text-blue-400 mt-3 flex items-center gap-1">
                          <FiZap className="w-3 h-3" />
                          This instance already has a notebook running - you can add another
                        </p>
                      )}
                    </div>
                  )}

                  {/* Advanced Options Toggle */}
                  {hasMultipleInstances && (
                    <button
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-4 transition-colors"
                    >
                      {showAdvanced ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                      {showAdvanced ? 'Hide' : 'Show'} other instances ({instances.length - 1} more)
                    </button>
                  )}

                  {/* Instance Selection (Advanced) */}
                  {showAdvanced && hasMultipleInstances && (
                    <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                      {instances.filter(i => i.instance_id !== selectedInstance).map((instance) => (
                        <div
                          key={instance.instance_id}
                          onClick={() => {
                            setSelectedInstance(instance.instance_id);
                            setShowAdvanced(false);
                          }}
                          className="p-3 rounded-md border border-gray-700 hover:border-gray-600 bg-gray-800/50 cursor-pointer transition-all"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-sm font-medium text-white">
                                {instance.project_name || instance.gpu_type}
                              </p>
                              <p className="text-xs text-gray-400">
                                {instance.instance_type} • ${(instance.hourly_cost ?? 0).toFixed(2)}/hr
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleQuickLaunch(instance.instance_id);
                              }}
                              className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                            >
                              Use this
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* Step 2: Data Sources placeholder */
            <div>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-base font-medium text-white">Connect Data Sources</h3>
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full">
                  Coming Soon
                </span>
              </div>
              <p className="text-sm text-gray-400 mb-5">
                Connect live feeds, upload datasets, or link databases directly to your notebook environment.
              </p>

              <div className="space-y-3">
                {[
                  { icon: FiRadio, label: 'Live Market Data', desc: 'Real-time price feeds, order books, and tick data' },
                  { icon: FiUploadCloud, label: 'Custom Datasets', desc: 'Upload CSV, Parquet, or HDF5 files to your workspace' },
                  { icon: FiDatabase, label: 'Database Connections', desc: 'Connect to PostgreSQL, ClickHouse, or TimescaleDB' },
                ].map(({ icon: Icon, label, desc }) => (
                  <div
                    key={label}
                    className="p-4 rounded-lg border border-gray-800 bg-gray-800/30 opacity-50 cursor-not-allowed"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-md bg-gray-700/50 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-300">{label}</span>
                          <FiLock className="w-3 h-3 text-gray-600" />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(instances.length > 0 || gpuOptions.length > 0) && (
          <div className="px-6 py-4 border-t border-gray-800 bg-gray-800/50">
            <div className="flex justify-between items-center">
              {step === 'compute' ? (
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-400 hover:text-white font-medium transition-colors"
                >
                  Cancel
                </button>
              ) : (
                <button
                  onClick={() => setStep('compute')}
                  className="px-4 py-2 text-gray-400 hover:text-white font-medium transition-colors flex items-center gap-1.5"
                >
                  <FiArrowLeft className="w-4 h-4" />
                  Back
                </button>
              )}

              {step === 'compute' ? (
                <button
                  onClick={() => setStep('data-sources')}
                  disabled={!canProceedFromCompute || loading}
                  className={`px-6 py-2.5 rounded-md font-medium flex items-center gap-2 transition-colors ${
                    !canProceedFromCompute || loading
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-white text-black hover:bg-gray-100'
                  }`}
                >
                  Next
                  <FiArrowRight className="w-4 h-4" />
                </button>
              ) : instances.length > 0 ? (
                <button
                  onClick={handleLaunch}
                  disabled={!selectedInstance || launching}
                  className={`px-6 py-2.5 rounded-md font-medium flex items-center gap-2 transition-colors ${
                    !selectedInstance || launching
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-white text-black hover:bg-gray-100'
                  }`}
                >
                  {launching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                      Launching...
                    </>
                  ) : (
                    <>
                      <FiPlay className="w-4 h-4" />
                      Skip & Launch
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleLaunchNewInstance}
                  disabled={gpuOptions.length === 0 || launching}
                  className={`px-6 py-2.5 rounded-md font-medium flex items-center gap-2 transition-colors ${
                    gpuOptions.length === 0 || launching
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {launching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Launching Instance...
                    </>
                  ) : (
                    <>
                      <FiZap className="w-4 h-4" />
                      Skip & Launch
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotebookLauncher;
